/**
 * Per-browser UI preferences: theme, density, rail collapsed. Persisted in localStorage.
 * There is no accent preference: the accent is the brand's (ADR 0061). A value saved under the
 * old `fd.accent` key is never read, so it changes nothing.
 */
import { create } from 'zustand';
import { applyDensity, applyMode, readStoredDensity, readStoredMode, resolveMode } from '@orgus/ui';
import type { DensityName, ThemeMode, ThemeName } from '@orgus/ui';

export type Theme = ThemeName;
export type Density = DensityName;

interface Prefs {
  /** The theme on screen now: light or dark (System resolved). */
  theme: Theme;
  /** What the person picked: light, dark or system. */
  mode: ThemeMode;
  setMode: (m: ThemeMode) => void;
  density: Density;
  railCollapsed: boolean;
  /** The rail's More group is open (ADR 0076). */
  railMore: boolean;
  helpOpen: boolean;
  setTheme: (t: Theme) => void;
  toggleTheme: () => void;
  setDensity: (d: Density) => void;
  setRailCollapsed: (v: boolean) => void;
  toggleRail: () => void;
  setRailMore: (v: boolean) => void;
  setHelpOpen: (v: boolean) => void;
}

function read(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}
function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value);
  } catch {
    // private mode: preferences last for this tab only
  }
}

const initialMode: ThemeMode = readStoredMode();
if (typeof document !== 'undefined') {
  applyMode(initialMode);
}
const initialDensity: Density = readStoredDensity();
if (typeof document !== 'undefined') document.documentElement.dataset.density = initialDensity;

export const usePrefs = create<Prefs>()((set, get) => ({
  theme: resolveMode(initialMode),
  mode: initialMode,
  density: initialDensity,
  railCollapsed: read('fd.rail') === 'collapsed',
  railMore: read('fd.rail.more') === 'open',
  helpOpen: false,
  setMode: (mode) => {
    applyMode(mode);
    set({ mode, theme: resolveMode(mode) });
  },
  setTheme: (theme) => get().setMode(theme === 'light' ? 'light' : 'dark'),
  toggleTheme: () => get().setMode(get().theme === 'dark' ? 'light' : 'dark'),
  setDensity: (density) => {
    applyDensity(density);
    set({ density });
  },
  setRailCollapsed: (v) => {
    write('fd.rail', v ? 'collapsed' : 'open');
    set({ railCollapsed: v });
  },
  toggleRail: () => get().setRailCollapsed(!get().railCollapsed),
  setRailMore: (v) => {
    write('fd.rail.more', v ? 'open' : 'closed');
    set({ railMore: v });
  },
  setHelpOpen: (helpOpen) => set({ helpOpen }),
}));

// System mode follows the computer live: keep the resolved theme in step for anything that reads it.
if (typeof matchMedia === 'function') {
  matchMedia('(prefers-color-scheme: light)').addEventListener('change', () => {
    const st = usePrefs.getState();
    if (st.mode === 'system') usePrefs.setState({ theme: resolveMode('system') });
  });
}
